import { DefaultResponseWithData } from "./system";
import { IPlannerVehicleRow, VehicleProductionLine } from "./ProductionPlanner";
import { DismantlingBayType } from "./DismantlingBay";
import { VehicleComplexity } from "./Vehicle";
import { PartAssemblyType } from "./Part";

export { PartAssemblyType };

export enum ProductionDashboardBayStatus {
  Available = 0,
  InProgress = 1,
  Complete = 2,
}

/** Distinguishes a real assembly column from the two optional non-assembly stages. */
export enum ProductionDashboardAssemblyKind {
  Assembly = 0,
  Triage = 1,
  Depollution = 2,
}

export interface IProductionDashboardAssembly {
  name: string;
  type: PartAssemblyType;
  kind: ProductionDashboardAssemblyKind;
}

export interface IBaysInUseKpi {
  used: number;
  total: number;
}

export interface IPartsRemovedBreakdownEntry {
  name: string;
  count: number;
}

export interface IPartsRemovedKpi {
  total: number;
  breakdown: IPartsRemovedBreakdownEntry[];
}

export interface IProductionDashboardKpis {
  baysInUse: IBaysInUseKpi;
  linesInUse: IBaysInUseKpi;
  vehiclesInProgress: number;
  vehiclesDismantledToday: number;
  /** Vehicles crushed today (Vehicle.ScrappedOn in today's UTC window). */
  vehiclesCrushedToday: number;
  /** Distinct vehicles whose depollution finished today. */
  vehiclesDepollutedToday: number;
  partsRemoved: IPartsRemovedKpi;
}

/** One assembly on one vehicle - the per-vehicle strip-down the bay cards are built from. */
export interface IProductionDashboardBayVehicleAssembly {
  /** Null for the Non-Assembly bucket. */
  partAssemblyId: string | null;
  name: string;
  type: PartAssemblyType;
  partsRemoved: number;
  partsTotal: number;
  /** Every part actioned; false for an assembly with no parts on this vehicle. */
  isComplete: boolean;
  isCurrent: boolean;
  /** Worked seconds from closed timer segments, plus startedOn for the open one. */
  workedSeconds: number;
  startedOn: string | null;
  targetMinutes: number | null;
  warningMinutes: number | null;
}

export interface IProductionDashboardBayVehicle {
  id: string;
  vrm: string;
  stockNumber: string | null;
  combinedMake: string | null;
  combinedModel: string | null;
  bodyStyle: string | null;
  complexity: VehicleComplexity;
  productionLine: VehicleProductionLine;
  partsRemoved: number;
  partsTotal: number;
  percentComplete: number;
  currentAssemblyName: string | null;
  currentAssemblyIndex: number | null;
  assemblyCount: number;
  currentAssemblyPartsRemoved: number;
  currentAssemblyPartsTotal: number;
  currentAssemblyElapsedSeconds: number;
  currentAssemblyStartedOn: string | null;
  currentAssemblyAverageMinutes: number | null;
  currentAssemblyWarningMinutes: number | null;
  /**
   * Every assembly this vehicle goes through, in flow order (Non-Assembly last when present).
   * assemblyCount is its length and currentAssemblyIndex the 1-based position of the isCurrent
   * entry. Empty from an API that predates it.
   */
  assemblies: IProductionDashboardBayVehicleAssembly[];
}

/** Why a station-history segment started or ended. Numeric: the API serialises enums as ints. */
export enum VehicleStationHistoryReason {
  Allocated = 0,
  Moved = 1,
  Unallocated = 2,
  Crushed = 3,
  TimerStart = 4,
  TimerFinish = 5,
  SupersededByTimer = 6,
  Manual = 7,
  SupersededByManual = 8,
  Sweep = 9,
}

/**
 * One station spell from the vehicle station history, placed on the bay the vehicle was on when
 * it began - so a vehicle crushed or re-planned later in the day keeps its place. The dashboard
 * groups the spells per vehicle into one bar.
 */
export interface IProductionDashboardTimelineEntry {
  dismantlingBayId: string;
  vehicleId: string;
  vrm: string | null;
  combinedMake: string | null;
  combinedModel: string | null;
  partAssemblyId: string;
  /** Null when the assembly has since been deleted. */
  partAssemblyName: string | null;
  /** What put the vehicle at this station: a timer start or a manual move. */
  source: VehicleStationHistoryReason;
  /** When the spell started (UTC ISO), clipped to the start of today for one left open overnight. */
  startedOn: string;
  /** When it ended; null while the vehicle is still at the station. */
  endedOn: string | null;
  isComplete: boolean;
}

export interface IProductionDashboardBay {
  id: string;
  name: string;
  type: DismantlingBayType;
  line: VehicleProductionLine;
  status: ProductionDashboardBayStatus;
  currentVehicle: IProductionDashboardBayVehicle | null;
  currentVehicles: IProductionDashboardBayVehicle[];
  dueNext: IPlannerVehicleRow[];
}

export interface IProductionDashboardSession {
  id: string;
  vehicleId: string;
  vrm: string | null;
  stockNumber: string | null;
  combinedMake: string | null;
  combinedModel: string | null;
  elapsedSeconds: number;
  startedOn: string | null;
  /** The bay/line the vehicle is on today, or null when it isn't allocated to one. */
  dismantlingBayId: string | null;
}

export interface IProductionDashboardOverview {
  kpis: IProductionDashboardKpis;
  bays: IProductionDashboardBay[];
  assemblies: IProductionDashboardAssembly[];
  inProgressTriages: IProductionDashboardSession[];
  inProgressDepollutions: IProductionDashboardSession[];
  timeline: IProductionDashboardTimelineEntry[];
}

export interface IProductionDashboardOverviewResponse
  extends DefaultResponseWithData<IProductionDashboardOverview> {}

export type ProductionDashboardView = "Line" | "Bays";

/** Median time a vehicle takes through one station over a chosen period. */
export interface IStationCycleTime {
  /** Null for the Triage and Depollution rows: they are not assemblies. */
  partAssemblyId: string | null;
  name: string;
  type: PartAssemblyType;
  /**
   * Whether this row is a real assembly or one of the two stages. The stage labels are rendered
   * from this rather than from name, so they read in the user language like the flow does.
   */
  kind: ProductionDashboardAssemblyKind;
  /** Flow order, so the table reads in the same direction as the line. */
  order: number;
  /** Vehicles that finished this station in the period — the sample size. */
  vehicleCount: number;
  /**
   * Median worked seconds per vehicle, or null when nothing came through in the period.
   * Null and 0 mean different things: "nobody used this station" vs "it took no time".
   */
  medianSeconds: number | null;
  /** The station's target, already converted from the minutes the API stores. */
  targetSeconds: number | null;
  /**
   * Median seconds a vehicle waited after finishing here before the next station started - the
   * queue between stations rather than the work at one. Null when nothing could be measured.
   */
  medianIdleSeconds: number | null;
  /**
   * False where there is nothing after the row to wait for: the last station in the flow, and the
   * two stages while they sit outside it. Those show N/A rather than a dash.
   */
  measuresIdle: boolean;
}

export interface IStationCycleTimes {
  from: string;
  to: string;
  stations: IStationCycleTime[];
}

export interface IStationCycleTimesResponse
  extends DefaultResponseWithData<IStationCycleTimes> {}
